// src/context/AuthContext.tsx

import React, { createContext, useState, useEffect, useContext } from 'react';
import { API_BASE_URL } from '../config'; 

// Define a estrutura do Usuário
export interface User {
  id: string;
  email: string;
  displayName: string;
  planId: string;
  role: string;
  photoURL?: string;
  isAdmin?: boolean;
  storageLimitGB?: number;
  storageUsedGB?: number;
}

interface AuthContextData {
  user: User | null;
  token: string | null;
  login: (token: string, userData?: User) => void;
  logout: () => void;
  updateUser: (user: User) => void; // <--- ADICIONADO: A função que faltava
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- FUNÇÃO NOVA: Permite atualizar o estado do usuário manualmente ---
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

// Função para buscar os dados mais recentes do servidor
  const refreshProfile = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;

    try {
        const response = await fetch(`${API_BASE_URL}/api/user/me`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${storedToken}`, // Envia token velho
                'Content-Type': 'application/json'
            }
        });

        if (response.ok) {
            const data = await response.json();
            
            // Se o backend retornou um novo token e usuário
            if (data.token && data.user) {
                // ATUALIZA TUDO: Token novo (com permissão) e Dados visuais
                login(data.token, data.user); 
                console.log("Sessão renovada com sucesso. Plano:", data.user.planId);
            }
        } else {
            if (response.status === 401) {
                logout();
            }
        }
    } catch (error) {
        console.error("Erro ao validar sessão:", error);
    }
  };
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      setToken(storedToken);
      if (storedUser) {
          try {
              setUser(JSON.parse(storedUser));
          } catch (e) {
              console.error("Erro no cache do usuário");
          }
      }
      
      refreshProfile().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = (newToken: string, newUserData?: User) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);

    let userToSave = newUserData;

    if (!userToSave) {
        try {
            const base64Url = newToken.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const decoded = JSON.parse(window.atob(base64));
            
            userToSave = {
                id: decoded.uid || decoded.id,
                email: decoded.email,
                displayName: decoded.displayName,
                planId: decoded.planId,
                role: decoded.role,
                photoURL: decoded.photoURL,
                isAdmin: decoded.isAdmin,
                storageLimitGB: decoded.storageLimitGB,
                storageUsedGB: decoded.storageUsedGB
            };
        } catch (e) {
            console.error("Erro ao decodificar token", e);
        }
    }

    if (userToSave) {
        updateUser(userToSave); // Usa a função centralizada
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    window.location.href = '/'; 
  };

  return (
    <AuthContext.Provider value={{ 
        user, 
        token, 
        login, 
        logout, 
        updateUser, // <--- Exportando a função para ser usada nas páginas
        isLoading,
        isAuthenticated: !!user,
        refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);